/**
 * Delete Recording Modal
 *
 * Destructive confirmation for deleting a course recording. Lists every file
 * that will be removed from the ingest and serving S3 buckets and requires the
 * user to type "DELETE" (case-insensitive) before the delete is enabled.
 */

import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiAlertTriangle, FiX, FiTrash2 } from 'react-icons/fi';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: var(--background-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1rem;

  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const ModalContent = styled(motion.div)`
  background: var(--background-primary);
  border-radius: 22px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.18);
  border: 1px solid var(--border-color);
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 480px) {
    border-radius: 16px;
    max-height: calc(100vh - 1.5rem);
  }
`;

const ModalHeader = styled.div`
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 480px) {
    padding: 1rem 1rem 0.75rem;
  }
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: var(--background-tertiary); color: var(--text-primary); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const WarningText = styled.div`
  color: var(--text-primary);
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 1rem;
`;

const BucketSection = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 14px;
  margin-bottom: 1rem;
  overflow: hidden;
`;

const BucketHeader = styled.div`
  background: var(--background-tertiary);
  padding: 0.65rem 0.9rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-secondary);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
`;

const BucketName = styled.span`
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  color: var(--text-primary);
  word-break: break-all;
`;

const FileList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 180px;
  overflow-y: auto;
`;

const FileItem = styled.li`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0.9rem;
  font-size: 0.8rem;
  border-top: 1px solid var(--border-color);
  &:first-child { border-top: none; }
`;

const FileKey = styled.span`
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  color: var(--text-primary);
  word-break: break-all;
`;

const FileSize = styled.span`
  color: var(--text-secondary);
  white-space: nowrap;
`;

const EmptyFiles = styled.div`
  padding: 0.75rem 0.9rem;
  font-size: 0.8rem;
  color: var(--text-secondary);
`;

const ConfirmLabel = styled.label`
  display: block;
  margin: 1.25rem 0 0.4rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const ConfirmInput = styled.input`
  width: 100%;
  padding: 0.7rem 0.9rem;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  font-size: 0.95rem;
  color: var(--text-primary);
  background: var(--background-primary);
  &:focus { outline: none; border-color: var(--error-color); box-shadow: 0 0 0 3px rgba(252, 129, 129, 0.18); }
`;

const ErrorText = styled.div`
  color: var(--error-color);
  font-size: 0.85rem;
  margin-top: 0.75rem;
`;

const LoadingText = styled.div`
  color: var(--text-secondary);
  font-size: 0.9rem;
  padding: 0.5rem 0 0.75rem;
`;

const ModalFooter = styled.div`
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    padding: 1rem;
    padding-bottom: calc(1rem + env(safe-area-inset-bottom));
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.7rem 1.25rem;
  border: 1px solid transparent;
  border-radius: 999px;
  font-weight: 500;
  cursor: pointer;
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;

  @media (max-width: 480px) {
    width: 100%;
    min-height: 44px;
  }

  &.secondary {
    background: var(--background-primary);
    color: var(--text-secondary);
    border-color: var(--border-color);
    &:hover { background: var(--background-tertiary); color: var(--text-primary); }
  }

  &.danger {
    background: linear-gradient(135deg, var(--error-color) 0%, var(--error-dark) 100%);
    color: var(--text-inverse);
    border-color: var(--error-color);
    &:hover:not(:disabled) { transform: translateY(-1px); }
    &:disabled { opacity: 0.55; cursor: not-allowed; }
  }
`;

const formatBytes = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) { value /= 1024; i += 1; }
  return `${value.toFixed(1)} ${units[i]}`;
};

const Bucket = ({ label, bucket, files }) => (
  <BucketSection>
    <BucketHeader>
      <span>{label}</span>
      {bucket && <BucketName>{bucket}</BucketName>}
    </BucketHeader>
    {files && files.length > 0 ? (
      <FileList>
        {files.map((file) => (
          <FileItem key={file.key}>
            <FileKey>{file.key}</FileKey>
            <FileSize>{formatBytes(file.size)}</FileSize>
          </FileItem>
        ))}
      </FileList>
    ) : (
      <EmptyFiles>No files found in this bucket.</EmptyFiles>
    )}
  </BucketSection>
);

function DeleteRecordingModal({
  isOpen,
  loading = false,
  deleting = false,
  error = null,
  recording = null,
  serving = null,
  ingest = null,
  confirmText = '',
  onConfirmTextChange,
  onConfirm,
  onClose,
  // Customizable so the same modal can power both delete and overwrite prompts.
  title = 'Delete recording',
  description = null,        // optional node; falls back to the delete wording
  confirmWord = 'delete',    // the word the user must type
  confirmLabel = 'Confirm Delete',
  busyLabel = 'Deleting…',
  // Destructive confirmations require an explicit choice — clicking the backdrop
  // does NOT dismiss. Use Cancel / the × / Confirm instead.
  closeOnOverlayClick = false,
}) {
  const word = (confirmWord || 'delete').trim();
  const wordUpper = word.toUpperCase();
  const wordPattern = new RegExp(`^${word}$`, 'i');
  const canSubmit = wordPattern.test((confirmText || '').trim()) && !loading && !deleting;
  const servingFiles = serving?.files || [];
  const ingestFiles = ingest?.files || [];
  const totalFiles = servingFiles.length + ingestFiles.length;

  const defaultDescription = (
    <>
      This permanently deletes
      {recording?.title ? <> <strong>“{recording.title}”</strong></> : ' this recording'}
      {' '}and all of its files from storage. This action cannot be undone.
    </>
  );

  return (
    <AnimatePresence>
      {isOpen && (
      <ModalOverlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(closeOnOverlayClick && !deleting) ? onClose : undefined}
      >
        <ModalContent
          initial={{ opacity: 0, scale: 0.96, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 16 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHeader>
            <ModalTitle>
              <FiAlertTriangle size={20} color="var(--error-color)" />
              {title}
            </ModalTitle>
            <CloseButton onClick={onClose} disabled={deleting}>
              <FiX size={18} />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            <WarningText>
              {description || defaultDescription}
            </WarningText>

            {loading ? (
              <LoadingText>Loading associated files…</LoadingText>
            ) : (
              <>
                <Bucket
                  label="Serving bucket"
                  bucket={serving?.bucket}
                  files={servingFiles}
                />
                {ingest?.sharedWithServing ? null : (
                  <Bucket
                    label="Ingest bucket"
                    bucket={ingest?.bucket}
                    files={ingestFiles}
                  />
                )}
                {totalFiles === 0 && (
                  <WarningText style={{ marginTop: '0.25rem', color: 'var(--text-secondary)' }}>
                    No stored files were found for this recording — only the database entry will be removed.
                  </WarningText>
                )}
              </>
            )}

            <ConfirmLabel htmlFor="delete-recording-confirm">
              Type <strong>{wordUpper}</strong> to confirm deletion
            </ConfirmLabel>
            <ConfirmInput
              id="delete-recording-confirm"
              type="text"
              value={confirmText}
              autoComplete="off"
              placeholder={wordUpper}
              disabled={deleting}
              onChange={(e) => onConfirmTextChange && onConfirmTextChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) onConfirm(); }}
              // Force the user to actually type the word — block paste/copy/cut/drop.
              onPaste={(e) => e.preventDefault()}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
              onDrop={(e) => e.preventDefault()}
            />

            {error && <ErrorText>{error}</ErrorText>}
          </ModalBody>

          <ModalFooter>
            <Button className="secondary" onClick={onClose} disabled={deleting}>
              Cancel
            </Button>
            <Button className="danger" onClick={onConfirm} disabled={!canSubmit}>
              <FiTrash2 size={16} />
              {deleting ? busyLabel : confirmLabel}
            </Button>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
      )}
    </AnimatePresence>
  );
}

export default DeleteRecordingModal;
